import { Utils } from 'src/app/utils';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { DeploymentResultExt } from 'src/app/model/SolutionInfo';
import { ActivityResponse, ActivityService } from 'src/app/service/activity.service';
import { EventService } from 'src/app/service/event.service';
import { Events, MsgPayLoad } from 'src/app/service/websocket.service';
import { ActionMenuEvent, ActionMenuIcon, ActionMenuItem } from '../controls/actionmenu/action-menu.component';
import { ColumnSort, ColumnType, DataTableColumn, DataTableFilter, DataTableFilterOption, DataTableRow, FilterType } from '../controls/datatable/datatable.component';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { ActivityDeploymentStepDetailsComponent } from '../dialogs/activity-deployment-step-details/activity-deployment-step-details.component';
import { PageCount, PageEvent } from '../controls/pagination/pagination.component';
import { Tab, TabColor } from '../controls/tabs/tabs.component';
import { AuditField } from '../settings-audit/settings-audit.component';
import { DataTableService } from 'src/app/service/data-table.service';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { BaseComponent } from 'src/app/model/BaseComponent';
import { SolutionService } from 'src/app/service/solution.service';

enum ActivitiesExecutionDetailsState {INIT, VIEW_DETAILS}
enum ActivityExecutionPropertyTab {GENERAL, STEPS}
@Component({
    selector: 'app-activities-execution',
    templateUrl: './activities-execution.component.html',
    styleUrls: ['./activities-execution.component.css', '../activities/activities.component.css'],
    host: {'animate.enter': 'enter-animation'},
    standalone: false
})
export class ActivitiesExecutionComponent extends BaseComponent implements OnInit {
  tabs: Tab[] = [];
  TabColor = TabColor;

  generalTab = new Tab('otsc-activity-execution-general-tab', $localize`General`, undefined, false);
  stepsTab = new Tab('otsc-activity-execution-steps-tab', $localize`Steps`, undefined, false);
  activityExecutionTabs: Tab[] = [this.generalTab, this.stepsTab];

  cols: Array<DataTableColumn>;
  activityResponse: ActivityResponse;
  deployments: DataTableRow[] = [];
  total: number = 0;
  ActivitiesExecutionDetailsState = ActivitiesExecutionDetailsState;
  detailState:ActivitiesExecutionDetailsState = ActivitiesExecutionDetailsState.INIT;
  ActivityExecutionPropertyTab = ActivityExecutionPropertyTab;
  activePropertyTab: ActivityExecutionPropertyTab = ActivityExecutionPropertyTab.GENERAL;
  currentDeploymentResult:DeploymentResultExt;
  deploymentsTableFilter:DataTableFilter[] = [];
  filterOpen: boolean = false;
  filterApplied: boolean = false;
  currentDeploymentsTableFilter:string = "";
  showRemoveAllPlanActivities: boolean = false;
  
  //steps table
  deploymentStepsCol: DataTableColumn[] = [];
  deploymentStepsRow: DataTableRow[] = [];

  //subscription
  eventMessageSubscription: Subscription;
  solutionInfoSubscription: Subscription;
  filterSubscription: Subscription;
  applyFilterSubscription: Subscription;
  cancelFilterSubscription: Subscription;

  constructor(private activityService: ActivityService, protected eventService: EventService,
              private router: Router, private dataTableSrv: DataTableService, protected modal: Modal,
              protected authService:AuthenticationService, protected solutionService: SolutionService) {
      super(eventService, modal, authService, solutionService);
      this.cols = Array<DataTableColumn>(
        new DataTableColumn("deploymentId", $localize`Id`, ColumnType.STRING, false, "5"),
        new DataTableColumn("executionPlanName", $localize`Name`, ColumnType.STRING, false, "15"),
        new DataTableColumn("statusMessage", $localize`Message`, ColumnType.STRING, false, "33"),
        new DataTableColumn("statusIcon", $localize`Status`, ColumnType.STATUSEXT, false, "7"),
        new DataTableColumn("username", $localize`Initiated by`, ColumnType.STRING, false, "10"),
        new DataTableColumn("createdDateTime", $localize`Created`, ColumnType.DATEFROMNOW, false, "10"),
        new DataTableColumn("updatedDateTime", $localize`Updated`, ColumnType.DATEFROMNOW, true, "10", ColumnSort.DESC),
        new DataTableColumn("duration", "Duration", ColumnType.DURATION, false, "10")
      );

      this.deploymentStepsCol = Array<DataTableColumn>(
        new DataTableColumn("stepNo", $localize`No.`, ColumnType.STRING, false, "10"),
        new DataTableColumn("nickname", $localize`Name`, ColumnType.STRING, false, "30"),
        new DataTableColumn("label", $localize`Label`, ColumnType.STRING, false, "25"),
        new DataTableColumn("product", $localize`Product`, ColumnType.STRING, false, "15"),
        new DataTableColumn("macAddress", $localize`System`, ColumnType.STRING, false, "15"),
        new DataTableColumn("uiStatus", $localize`Status`, ColumnType.STATUSEXT, false, "10")
      );
    }

  ngOnInit(): void {
    this.currentUserAuthInfo = this.authService.getLoggedInUser();

    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.solutionInfo = newSolutionInfo;
      this.title = `${this.solutionInfo.currentEnvironment.solName} : ${this.solutionInfo.currentEnvironment.name}`;
      this.getDeployments("", PageCount.TWENTYFIVE, 0);
    });

    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      if(msg.eventType == Events.ActivityRefresh){
        if(this.currentDeploymentResult && this.currentDeploymentResult.executionStatus !== 'COMPLETED' &&
        this.currentDeploymentResult.executionStatus !== 'ERROR'){
          this.activityService.getDeploymentStatus(this.currentDeploymentResult.deploymentId).pipe(
            tap(resp => {
              this.setCurrentDeploymentResult(resp.body, false);
            }),
            finalize(() => {
              this.loading = false;
            })).subscribe();
        }else{
          this.getDeployments("", PageCount.TWENTYFIVE, 0);
        }
      }
    });

    this.filterSubscription = this.dataTableSrv.filterClicked$.subscribe((isOpen) => {
      this.filterOpen = isOpen;
      if (!this.filterOpen && this.filterApplied) {
        this.getDeployments("", PageCount.TWENTYFIVE, 0);
        this.filterApplied = false;
      }
    });
    
    this.applyFilterSubscription = this.dataTableSrv.applyFilter$.subscribe((appliedFilters: DataTableFilter[]) => {
      this.currentDeploymentsTableFilter = appliedFilters[0].selected;
      this.getDeployments(appliedFilters[0].selected, PageCount.TWENTYFIVE, 0);
      this.filterApplied = true;
    });

    this.cancelFilterSubscription = this.dataTableSrv.cancelFilter$.subscribe(() => {
      this.getDeployments("", PageCount.TWENTYFIVE, 0);
      this.filterApplied = false;
    });
  }

  ngOnDestroy(){
    this.eventMessageSubscription.unsubscribe();
    this.solutionInfoSubscription.unsubscribe();
    this.filterSubscription.unsubscribe();
    this.applyFilterSubscription.unsubscribe();
    this.cancelFilterSubscription.unsubscribe();
  }

  getDeploymentStatus(deploymentResult:DeploymentResultExt): any {
    let status:any;

    if(deploymentResult.executionStatus == 'COMPLETED'){
      status = {className: 'otsc-icon-status-success', altText: $localize`Completed`};
    }else if(deploymentResult.executionStatus == 'ERROR'){
      status = {className: 'otsc-icon-status-error', altText: $localize`Complete with errors`};
    }else if(deploymentResult.executionStatus == 'CANCELED'){
      status = {className: 'otsc-icon-status-cancelled', altText: $localize`Canceled`};
    }else if(deploymentResult.executionStatus == 'RUNNING'){
      status = {className: 'otsc-icon-status-loading-spinner', altText: $localize`Running`};
    }else if(deploymentResult.executionStatus == 'QUEUED'){
      status = {className: 'otsc-icon-status-waiting', altText: $localize`Queued`};
    }else{
      status = {className: '', altText: ''};
    }

    return status;
  }

  getDeploymentStatusMessage(deployment:DeploymentResultExt): string {
    let statusMsg:string = '';
    
    if((deployment.executionStatus != 'CANCELED' && deployment.status && deployment.status.success) || deployment.executionStatus == 'QUEUED' || 
       (deployment.executionStatus != 'CANCELED' && deployment.status && !deployment.status.success && !deployment.status.completed)){
      statusMsg = deployment.statusMessage;
    }else if(deployment.executionStatus != 'CANCELED' && deployment.executionStatus != 'QUEUED' && deployment.status && !deployment.status.success){
      statusMsg = deployment.status.errorMessage;
    }else if(deployment.executionStatus == 'CANCELED'){
      statusMsg = $localize`Canceled`;
    }

    return statusMsg;
  }

  formatDeployment(deploymentResult:DeploymentResultExt): DataTableRow {    
    let actions:ActionMenuItem[] = [new ActionMenuItem("Details", $localize`Details`, ActionMenuIcon.DETAILS, deploymentResult, false)];
    
    if (deploymentResult.executionStatus == 'RUNNING'){
      actions.push(new ActionMenuItem("Cancel", $localize`Cancel`, ActionMenuIcon.CANCEL, deploymentResult, false));
    }

    return new DataTableRow({deploymentId: deploymentResult.deploymentId,
                            executionPlanName: deploymentResult.executionPlanName,
                            statusMessage: this.getDeploymentStatusMessage(deploymentResult),
                            statusIcon: this.getDeploymentStatus(deploymentResult),
                            executionStatus: deploymentResult.executionStatus,
                            username: deploymentResult.initiatorUserName,
                            createdDateTime: deploymentResult.createdDateTime,
                            updatedDateTime: deploymentResult.updatedDateTime,
                            steps: deploymentResult.steps,
                            initiatorUserName: deploymentResult.initiatorUserName,
                            status: deploymentResult.status,
                            executionPlanId: deploymentResult.executionPlanId,
                            duration: deploymentResult.duration
                          },
                             actions);
  }

  formatDeployments(): DataTableRow[] {
    let rows:DataTableRow[] = [];

    this.activityResponse.deployments.forEach((deployment) => {
      rows.push(this.formatDeployment(deployment));
    });

    return rows;
  }

  createOtherDeploymentsTableFilter():DataTableFilter[] {
    let activityFilter: DataTableFilterOption[] = [];
    this.activityResponse.filters.forEach( filter => {
      switch (filter) {
        case 'CANCELED':
          activityFilter.push(new DataTableFilterOption($localize`Canceled`, filter));
          break;
        case 'COMPLETED':
          activityFilter.push(new DataTableFilterOption($localize`Completed`, filter));
          break;
        case 'QUEUED':
          activityFilter.push(new DataTableFilterOption($localize`Queued`, filter));
          break;
        case 'ERROR':
          activityFilter.push(new DataTableFilterOption($localize`Error`, filter));
          break;
        case 'RUNNING':
          activityFilter.push(new DataTableFilterOption($localize`Running`, filter));
          break;
        default:
          activityFilter.push(new DataTableFilterOption(filter, filter));
          break;
      }
    });
    activityFilter.unshift(new DataTableFilterOption($localize`All activities`, ""));
    activityFilter.sort((a, b) => (a.text > b.text ? 1 : a.text == b.text ? 0 : -1));

    return Array<DataTableFilter>(
      new DataTableFilter("other-deployment-filter-status",
                          AuditField.TYPE,
                          $localize`Status`, 
                          FilterType.DROPDOWN, 
                          activityFilter)
    );
  }

  getDeployments(statusType: string, itemsPerPage:number, startingIndex:number): void {
    this.activityService.listDeployment(statusType, itemsPerPage, startingIndex).pipe(
      tap(resp => {
        this.activityResponse = resp.body;
        this.total = this.activityResponse.numActivity;
        this.deployments = this.formatDeployments();
        this.deploymentsTableFilter = this.createOtherDeploymentsTableFilter();
        this.showRemoveAllPlanActivities = this.activityResponse.numActivity > 0;
      }),
      finalize(() => {
        this.loading = false;
      })
    ).subscribe();
  }

  goBack():void {
    this.currentDeploymentResult = undefined;
    this.getDeployments("", PageCount.TWENTYFIVE, 0);
    this.detailState = ActivitiesExecutionDetailsState.INIT;
  }

  onSelectedDataTabChange(index: number) {
    this.activePropertyTab = index;
  }

  setCurrentDeploymentResult(result:DeploymentResultExt, resetTab:boolean): void {
    if(resetTab){
      setTimeout(() => {
        this.activePropertyTab = ActivityExecutionPropertyTab.GENERAL;
      }, 0);
    }
    this.deploymentStepsRow = [];
    this.currentDeploymentResult = result;

    if(this.currentDeploymentResult && this.currentDeploymentResult.steps){
      this.currentDeploymentResult.steps.forEach( (step, index) => {
        this.deploymentStepsRow.push(new DataTableRow({stepNo: index + 1,
                                                       nickname: step.nickname,
                                                       label: step.label,
                                                       product: step.product,
                                                       macAddress: step.macAddress, 
                                                       uiStatus: Utils.getDeploymentStepStatus(this.currentDeploymentResult, step.status)}, [new ActionMenuItem("Details", $localize`Details`, ActionMenuIcon.DETAILS, step, false)]));
      });
    }
  }

  deploymentStepActionMenuClicked(event: ActionMenuEvent): void {
    if(event.name === "Details"){
      this.viewStepDetails(event.data[0]);
    }
  }

  deploymentStepRowlicked(event:any): void {
    if(event.actions && event.actions.length == 1){
      this.viewStepDetails(event.actions[0].data);
    }
  }

  viewStepDetails(row:DataTableRow): void {
    this.modal.open(ActivityDeploymentStepDetailsComponent, {
      data: {step: row,
             id: 'otsc-activity-deployment-step-detail-dialog',
             currentDeploymentResult: this.currentDeploymentResult}
    });
  }

  actionMenuClicked(event: ActionMenuEvent): void {
    if(event.name === "Details"){
      this.detailState = ActivitiesExecutionDetailsState.VIEW_DETAILS;
      this.setCurrentDeploymentResult(event.data[0], true);
    }else if(event.name === "Cancel"){
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Cancel execution plan`,
            message: $localize`Are you sure you want to cancel this plan?<br/> <b>Note:</b> The selected execution plan will be cancelled but the current step/agent will run to completion.`,
            type: ModalMessageType.Question,
            primaryBtn: $localize`Yes`,
            secondaryBtn: $localize`No`,
            width: '475px'
          },
          id: 'otsc-executionplan-cancel-task-dlg'
        }
        ).afterClosed.subscribe((result: boolean) => {
          if (result) {
            this.activityService.cancelDeployment(event.data[0].deploymentId).pipe(
              tap(resp => {
                this.getDeployments("", PageCount.TWENTYFIVE, 0);
                Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`${event.data[0].executionPlanName} was canceled successfully.`);
              })
            ).subscribe();
          }
          sub.unsubscribe();
        });
    }
  }

  onPageChange(pageEvent: PageEvent) {
    this.getDeployments(this.filterApplied ? this.currentDeploymentsTableFilter : "", pageEvent.itemsPerPage, pageEvent.startingIndex);
  }

  viewPlan(): void {
    this.router.navigate(['/plans/details/' + this.currentDeploymentResult.executionPlanId]);
  }

  activitiesRowClick(event: any): void {
    this.detailState = ActivitiesExecutionDetailsState.VIEW_DETAILS;
    this.setCurrentDeploymentResult(event.data, true);
  }

  removeAllPlanActivities(event: any): void {
    let msg: string = $localize`You are about to remove all plan activities with the exception of 'Queued' or 'Running' items. Do you wish to proceed?`;
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Remove`,
          message: msg,
          type: ModalMessageType.Question,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-remove-all-plan-activities-dialog'
      }).afterClosed.subscribe((result: any) => {
        if (result) {
          let bannerMsg: MsgPayLoad;
          this.activityService.removeAllPlanActivities().pipe(
            tap(resp => {
              bannerMsg = new MsgPayLoad(undefined, {
                message: $localize`Plan activities were removed successfully.`
              });

              bannerMsg.eventType = Events.SuccessMsg;
              this.getDeployments("", PageCount.TWENTYFIVE, 0);
            }),
            catchError(resp => {
              bannerMsg = new MsgPayLoad(undefined, {
                message: $localize`Error removing plan activities. Please try again later.`
              });

              bannerMsg.eventType = Events.ErrorMsg;
              return of(resp);
            }),
            finalize(() => {
              Utils.addHeaderMessageBanner(this.eventService, bannerMsg);
            }),
          ).subscribe();
        }
        sub.unsubscribe();
      }
      );
  }

  onActivityListKeyDown(event: KeyboardEvent, row: DataTableRow, index: number): void {
    let activityElements: NodeListOf<Element> = document.getElementById('activityExecutionList').querySelectorAll('.ot-list__item');
    switch (event.key) {
      case "ArrowUp":
        if ((index - 1) > -1) {
          (activityElements[index - 1] as HTMLLIElement).focus();
        }
        break;
      case "ArrowDown":
        if ((index + 1) < activityElements.length) {
          (activityElements[index + 1] as HTMLLIElement).focus();
        }
        break;
    }
  }
}